import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  StyledLabel,
  StyledInput,
  StyledRow,
  StyledContent,
} from "./radio.styles";

export const RadioContext = createContext<
  | { currentValue: string; handleSetCurrentValue: (value: string) => void }
  | undefined
>(undefined);

const useRadioContext = () => {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error(
      "Radio compound components cannot be rendered outside the Radio component",
    );
  }
  return context;
};

export type RadioGroupProps = {
  children: ReactNode;
  onChange: (value: string) => void;
  defaultValue?: string;
  pt?: number;
};

export const RadioGroup: React.FC<RadioGroupProps> & {
  RadioButton: React.FC<RadioButtonProps>;
} = ({ children, onChange, defaultValue, pt }) => {
  const [currentValue, setCurrentValue] = useState("");

  const handleSetCurrentValue = useCallback(
    (value: string) => {
      setCurrentValue(value);
      onChange(value);
    },
    [onChange],
  );

  useEffect(() => {
    if (defaultValue) {
      setCurrentValue(defaultValue);
    }
  }, [defaultValue]);

  return (
    <RadioContext.Provider value={{ currentValue, handleSetCurrentValue }}>
      <StyledRow {...(pt && { pt })}>{children}</StyledRow>
    </RadioContext.Provider>
  );
};

export type RadioButtonProps = {
  name: string;
  testId?: string;
  id?: string;
  children: ReactNode;
};

const RadioButton: React.FC<RadioButtonProps> = ({
  children,
  name,
  testId,
  id,
}) => {
  const { currentValue, handleSetCurrentValue } = useRadioContext();
  const checked = name === currentValue;

  return (
    <StyledLabel>
      <StyledInput
        value={name}
        checked={checked}
        type="radio"
        name={name}
        onChange={(e) => handleSetCurrentValue(e.target.value)}
        {...(testId && { "data-testid": testId })}
        {...(id && { id })}
      />
      <StyledContent>{children}</StyledContent>
    </StyledLabel>
  );
};

RadioGroup.RadioButton = RadioButton;
